import { useCreateOneComment } from "#src/components/hooks/adapters/useComments";
import { useFile } from "#src/hooks/useFile";
import { ExceptionUtils } from "#src/utils/exception";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FileUploadInput,
  Form,
  TextAreaInput,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  CommentEntity,
  CommentFormSchema,
  CommentSchemaType,
  Resources,
  type CommentFormType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { default as React, useState } from "react";
import styles from "./CommentForm.module.css";

const cx = classNames.bind(styles);

export const CommentForm = ({
  entity_type = CommentEntity.WORKFLOW,
  entity_id,
  additional_identifier = "",
  title_context,
  canCreateComments = false,
  onCreateCallback,
  shouldShowForm,
  onSubmitSuccess,
  onCancel,
}: Partial<
  Pick<
    CommentSchemaType,
    "entity_type" | "entity_id" | "additional_identifier" | "title_context"
  > & {
    canCreateComments?: boolean;
    onCreateCallback: (newCommentId: string) => void;
    shouldShowForm?: boolean;
    onSubmitSuccess?: () => void;
    onCancel?: () => void;
  }
>) => {
  const { handleFileUpload, handleFileDownload } = useFile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isFormShown, setIsFormShown] = useState(false);

  const form = useForm<CommentFormType>({
    defaultValues: { text: "", files: [] },
    mode: "onChange",
  });

  const createCommentMutation = useCreateOneComment({
    onSuccess: (response: { data: CommentSchemaType }) => {
      queryClient.invalidateQueries({
        queryKey: [Resources.COMMENT],
      });
      onCreateCallback?.(response.data.id);
      onSubmitSuccess?.();
    },
    onError: (error: unknown) => {
      ExceptionUtils.reportException(error, "error", {
        hint: "Error creating comment",
      });
      toast.push({
        intent: "error",
        description: "Failed to create comment.",
      });
    },
    noAlerts: true,
  });

  const onSubmit = () => {
    createCommentMutation.mutate({
      data: {
        text: form.getValues(CommentFormSchema.keyof().Enum.text),
        title_context: title_context,
        files: form
          .getValues(CommentFormSchema.keyof().Enum.files)
          ?.map((file) => {
            return { file_id: file.ref, file_name: file.name };
          }),
      },
      meta: {
        entity_type,
        entity_id,
        additional_identifier,
      },
    });

    form.reset();
  };

  const handleCancel = () => {
    onCancel?.();
    setIsFormShown(false);
    form.reset();
  };

  const isVisible = shouldShowForm ?? isFormShown;

  return (
    <>
      {isVisible ? (
        <div className={cx("container")}>
          <div>
            <Form
              {...form}
              onSubmit={onSubmit}
            >
              <TextAreaInput
                name={CommentFormSchema.keyof().Enum.text}
                label="Comment"
                placeholder="Enter Comment..."
                isLabelShown={false}
                isRequired
                autoFocus
              />
              <FileUploadInput
                name={CommentFormSchema.keyof().Enum.files}
                id={CommentFormSchema.keyof().Enum.files}
                label="Files"
                isLabelShown={false}
                isRequired={false}
                allowMultiple
                hasTrigger
                uploadFile={handleFileUpload}
                downloadFile={handleFileDownload}
              />
            </Form>
          </div>
          <div className={cx("button-container")}>
            <Button
              className={cx("button")}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className={cx("button")}
              variant="primary"
              onClick={onSubmit}
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                !canCreateComments
              }
            >
              Comment
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className={cx("button")}
          onClick={() => setIsFormShown(true)}
          disabled={shouldShowForm}
        >
          Comment
        </Button>
      )}
    </>
  );
};
